import React, { useRef, useCallback, useLayoutEffect } from 'react';
import cn from 'classnames';
import * as styles from './Collapse.module.scss';

const CHECK_TIMEOUT = 50;

function setRestStyles(container, isOpened, contentHeight) {
  const hasOpened = isOpened && container.style.height === `${contentHeight}px`;
  const hasClosed = !isOpened && container.style.height === '0px';

  if (hasOpened || hasClosed) {
    container.style.overflow = isOpened ? 'initial' : 'hidden';
    container.style.height = isOpened ? 'auto' : '0px';
  }
}

function setWorkStyles(container, isOpened, contentHeight) {
  const isOpening = isOpened && container.style.height === `${contentHeight}px`;
  const isClosing = !isOpened && container.style.height === '0px';

  if (!isOpening && !isClosing) {
    container.style.overflow = 'hidden';
    container.style.height = isOpened ? `${contentHeight}px` : '0px';
  }
}

export default function Collapse({ isOpened, children, className, id, labelledBy }) {
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const timeoutRef = useRef();
  const initialStyleRef = useRef(
    isOpened ? { height: 'auto', overflow: 'initial' } : { height: '0px', overflow: 'hidden' },
  );

  const onResize = useCallback(() => {
    clearTimeout(timeoutRef.current);
    const container = containerRef.current;
    const content = contentRef.current;
    if (!container || !content) return;

    const containerHeight = Math.floor(container.clientHeight);
    const contentHeight = Math.floor(content.clientHeight);
    const isFullyOpened = isOpened && Math.abs(contentHeight - containerHeight) <= 1;
    const isFullyClosed = !isOpened && Math.abs(containerHeight) <= 1;

    if (isFullyOpened || isFullyClosed) {
      setRestStyles(container, isOpened, contentHeight);
    } else {
      setWorkStyles(container, isOpened, contentHeight);
      timeoutRef.current = setTimeout(onResize, CHECK_TIMEOUT);
    }
  }, [isOpened]);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const content = contentRef.current;

    if (container && content && container.style.height === 'auto') {
      container.style.height = `${content.clientHeight}px`;
    }

    onResize();

    return () => clearTimeout(timeoutRef.current);
  }, [onResize]);

  return (
    <div
      ref={containerRef}
      id={id}
      className={cn(styles.collapse, className)}
      style={initialStyleRef.current}
      aria-labelledby={labelledBy}
      aria-hidden={!isOpened}
      inert={!isOpened ? true : undefined}
    >
      <div ref={contentRef}>{children}</div>
    </div>
  );
}
